// ---------------------------------------------------------------------------
// LayersPanel.js — upgraded "Straturi" panel for the shared Design Editor.
//
// Registered into the FOUNDATION seam (see DesignEditor.js header) via
// editor.addPanelSection(); the group/ungroup shortcuts via editor.registerCommand().
// This module owns ONLY this file (+ LayersPanel.css). It never binds a fabric
// canvas event directly — every mutation goes through the editor API:
//   editor.getObjects() / getActive() / canvas.moveTo(...)  (read + reorder)
//   editor.pushHistory(label) / requestRender() / refreshLayers()  (persist + repaint)
//   editor.getSerializationProps()  (clone with lock/visibility/name preserved)
//
// Re-render model: the foundation calls editor.refreshLayers() (a setState) on
// object:added/removed/modified and selection changes, which re-renders
// DesignEditor and re-invokes this section's render(editor). We derive the list
// fresh from editor.getObjects() every render, and after our own mutations we
// call editor.refreshLayers() to trigger the same re-render. Local UI state
// (inline rename) lives in this component and survives parent re-renders because
// the section keeps a stable position/key in the tree.
//
// Persistence: name / visible / selectable / evented / lock* are already in the
// foundation's BASE_SERIALIZATION_PROPS; we additionally register `hasControls`
// so a locked layer keeps its handles hidden across undo + save/reload.
// ---------------------------------------------------------------------------

import React, { useCallback, useRef, useState } from 'react';
import { SortableContainer, SortableElement, SortableHandle } from 'react-sortable-hoc';
import './LayersPanel.css';

// Stable React keys for fabric objects without mutating them (a WeakMap keyed by
// object reference — new objects after undo/reload simply get fresh ids).
const idMap = new WeakMap();
let nextId = 1;
function layerId(obj) {
  let id = idMap.get(obj);
  if (!id) { id = nextId++; idMap.set(obj, id); }
  return id;
}

const isLocked = (obj) => !!(
  obj.lockMovementX || obj.lockMovementY
  || obj.lockScalingX || obj.lockScalingY || obj.lockRotation
);

function typeLabel(type) {
  switch (type) {
    case 'i-text': case 'text': case 'textbox': return 'Text';
    case 'image': return 'Imagine';
    case 'group': return 'Grup';
    case 'activeSelection': return 'Selectie';
    case 'rect': return 'Dreptunghi';
    case 'circle': return 'Cerc';
    case 'ellipse': return 'Elipsa';
    case 'triangle': return 'Triunghi';
    case 'polygon': return 'Poligon';
    case 'line': return 'Linie';
    case 'path': return 'Desen';
    default: return 'Obiect';
  }
}

// ---------------------------------------------------------------------------
// Icons (inline, currentColor). Kept tiny so the 200px column stays readable.
// ---------------------------------------------------------------------------
const svg = (children, w = 14) => (
  <svg viewBox="0 0 24 24" width={w} height={w} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">{children}</svg>
);

const gripIcon = (
  <svg viewBox="0 0 16 16" width="12" height="12" fill="currentColor">
    <circle cx="6" cy="4" r="1.3" /><circle cx="10" cy="4" r="1.3" />
    <circle cx="6" cy="8" r="1.3" /><circle cx="10" cy="8" r="1.3" />
    <circle cx="6" cy="12" r="1.3" /><circle cx="10" cy="12" r="1.3" />
  </svg>
);
const eyeIcon = svg(<><path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z" /><circle cx="12" cy="12" r="3" /></>, 13);
const eyeOffIcon = svg(<><path d="M9.9 4.24A9.1 9.1 0 0112 4c7 0 11 8 11 8a18 18 0 01-3 3.72M6.6 6.6A18 18 0 001 12s4 8 11 8a9 9 0 004-.9" /><line x1="3" y1="3" x2="21" y2="21" /></>, 13);
const lockIcon = svg(<><rect x="5" y="11" width="14" height="9" rx="1.5" /><path d="M8 11V8a4 4 0 018 0v3" /></>, 12);
const unlockIcon = svg(<><rect x="5" y="11" width="14" height="9" rx="1.5" /><path d="M8 11V8a4 4 0 017.5-2" /></>, 12);
const dupIcon = svg(<><rect x="9" y="9" width="11" height="11" rx="2" /><path d="M5 15V5a2 2 0 012-2h10" /></>, 12);
const delIcon = svg(<><path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m2 0v14a2 2 0 01-2 2H8a2 2 0 01-2-2V6" /></>, 12);
const groupIcon = svg(<><rect x="3" y="3" width="8" height="8" rx="1" /><rect x="13" y="13" width="8" height="8" rx="1" /><path d="M11 7h4a2 2 0 012 2v4" /></>, 15);
const ungroupIcon = svg(<><rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="14" width="7" height="7" rx="1" /><line x1="7" y1="14" x2="7" y2="21" /><line x1="14" y1="7" x2="21" y2="7" /></>, 15);

function typeIcon(type) {
  switch (type) {
    case 'i-text': case 'text': case 'textbox':
      return svg(<><path d="M4 6V5h16v1M12 5v14M9 19h6" /></>);
    case 'image':
      return svg(<><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" /><path d="M21 15l-5-5L5 21" /></>);
    case 'group': case 'activeSelection':
      return svg(<><rect x="3" y="3" width="8" height="8" rx="1" /><rect x="13" y="13" width="8" height="8" rx="1" /></>);
    case 'circle': case 'ellipse':
      return svg(<circle cx="12" cy="12" r="9" />);
    case 'triangle': case 'polygon':
      return svg(<path d="M12 3l9 16H3z" />);
    case 'line':
      return svg(<line x1="5" y1="19" x2="19" y2="5" />);
    case 'path':
      return svg(<path d="M3 17.25V21h3.75L17.8 9.94l-3.75-3.75zM20.7 7a1 1 0 000-1.4l-2.3-2.3a1 1 0 00-1.4 0l-1.83 1.83 3.75 3.75z" />);
    default:
      return svg(<rect x="3" y="3" width="18" height="18" rx="2" />);
  }
}

// ---------------------------------------------------------------------------
// Group / ungroup — registered as Ctrl+G / Ctrl+Shift+G commands and exposed as
// header buttons. activeSelection.toGroup() / group.toActiveSelection() (5.3).
// ---------------------------------------------------------------------------
function groupSelection(editor) {
  const canvas = editor.canvas;
  const active = canvas.getActiveObject();
  if (!active || active.type !== 'activeSelection' || active.getObjects().length < 2) return;
  const group = active.toGroup();
  if (group && !group.name) group.name = 'Grup';
  if (group) canvas.setActiveObject(group);
  canvas.requestRenderAll();
  editor.pushHistory('Grupare');
  editor.refreshLayers();
}

function ungroupSelection(editor) {
  const canvas = editor.canvas;
  const active = canvas.getActiveObject();
  if (!active || active.type !== 'group') return;
  active.toActiveSelection();
  canvas.requestRenderAll();
  editor.pushHistory('Anulare grupare');
  editor.refreshLayers();
}

// ---------------------------------------------------------------------------
// Sortable primitives (module scope so the HOCs are stable across re-renders).
// ---------------------------------------------------------------------------
const DragHandle = SortableHandle(() => (
  <span className="de-lp-grip" title="Trage pentru reordonare" aria-label="Trage pentru reordonare">{gripIcon}</span>
));

const LayerRow = SortableElement((p) => (
  <div
    className={`de-lp-row${p.active ? ' active' : ''}${p.hidden ? ' hidden' : ''}`}
    onClick={() => p.onSelect(p.obj)}
    title={p.name}
  >
    <DragHandle />
    <span className="de-lp-icon">{typeIcon(p.type)}</span>
    {p.editing ? (
      <input
        ref={p.focusRename}
        className="de-lp-rename"
        value={p.draftName}
        onChange={(e) => p.onDraftChange(e.target.value)}
        onKeyDown={p.onRenameKeyDown}
        onBlur={() => p.onRenameBlur(p.obj)}
        onClick={(e) => e.stopPropagation()}
      />
    ) : (
      <span
        className="de-lp-name"
        onDoubleClick={(e) => { e.stopPropagation(); p.onStartRename(p.obj); }}
      >{p.name}</span>
    )}
    <span className="de-lp-tools">
      <button
        className={`de-lp-tool${p.hidden ? '' : ' on'}`}
        title={p.hidden ? 'Arata stratul' : 'Ascunde stratul'}
        onClick={(e) => { e.stopPropagation(); p.onToggleVis(p.obj); }}
      >{p.hidden ? eyeOffIcon : eyeIcon}</button>
      <button
        className={`de-lp-tool${p.locked ? ' on' : ''}`}
        title={p.locked ? 'Deblocheaza stratul' : 'Blocheaza stratul'}
        onClick={(e) => { e.stopPropagation(); p.onToggleLock(p.obj); }}
      >{p.locked ? lockIcon : unlockIcon}</button>
      <button
        className="de-lp-tool de-lp-hover-only"
        title="Dubleaza stratul"
        onClick={(e) => { e.stopPropagation(); p.onDuplicate(p.obj); }}
      >{dupIcon}</button>
      <button
        className="de-lp-tool danger de-lp-hover-only"
        title="Sterge stratul"
        onClick={(e) => { e.stopPropagation(); p.onDelete(p.obj); }}
      >{delIcon}</button>
    </span>
  </div>
));

const LayerList = SortableContainer((p) => (
  <div className="de-lp-list">
    {p.rows.map((r) => (
      <LayerRow key={r.key} index={r.displayIndex} disabled={r.editing} {...r.rowProps} />
    ))}
  </div>
));

// ---------------------------------------------------------------------------
// The panel component (function component with local rename state).
// ---------------------------------------------------------------------------
function LayersPanel({ editor }) {
  const [editingId, setEditingId] = useState(null);
  const [draftName, setDraftName] = useState('');
  const renameCancelRef = useRef(false);
  const rootRef = useRef(null);
  const canvas = editor.canvas;

  const objs = editor.getObjects();
  const n = objs.length;
  const active = editor.getActive();
  const activeMembers = active
    ? (active.type === 'activeSelection' ? active.getObjects() : [active])
    : [];
  const activeSet = new Set(activeMembers);
  const canGroup = !!(active && active.type === 'activeSelection' && active.getObjects().length >= 2);
  const canUngroup = !!(active && active.type === 'group');

  const focusRename = useCallback((el) => { if (el) { el.focus(); el.select(); } }, []);
  const onDraftChange = useCallback((v) => setDraftName(v), []);

  const startRename = (obj) => {
    renameCancelRef.current = false;
    setDraftName(obj.name || typeLabel(obj.type));
    setEditingId(layerId(obj));
  };

  // Keep the rename field isolated from the foundation's window keydown dispatch.
  const onRenameKeyDown = (e) => {
    e.stopPropagation();
    if (e.key === 'Enter') { e.preventDefault(); e.target.blur(); }
    else if (e.key === 'Escape') { e.preventDefault(); renameCancelRef.current = true; e.target.blur(); }
  };

  const commitRename = (obj) => {
    if (renameCancelRef.current) { renameCancelRef.current = false; setEditingId(null); return; }
    const name = draftName.trim();
    const prev = obj.name || '';
    setEditingId(null);
    if (name && name !== prev) {
      obj.name = name;
      editor.pushHistory('Redenumire strat');
      editor.refreshLayers();
    }
  };

  const onSelect = (obj) => {
    if (editingId) return;
    if (canvas.getActiveObject() === obj) return;
    canvas.discardActiveObject();
    canvas.setActiveObject(obj);
    editor.requestRender();
    editor.refreshLayers();
  };

  const onToggleVis = (obj) => {
    const wasVisible = obj.visible !== false;
    obj.visible = !wasVisible;
    editor.requestRender();
    editor.pushHistory(wasVisible ? 'Ascunde strat' : 'Arata strat');
    editor.refreshLayers();
  };

  const onToggleLock = (obj) => {
    const nowLocked = !isLocked(obj);
    obj.lockMovementX = nowLocked;
    obj.lockMovementY = nowLocked;
    obj.lockScalingX = nowLocked;
    obj.lockScalingY = nowLocked;
    obj.lockRotation = nowLocked;
    // Hide transform handles while locked (persisted via registerSerializationProps).
    obj.hasControls = !nowLocked;
    editor.requestRender();
    editor.pushHistory(nowLocked ? 'Blocare strat' : 'Deblocare strat');
    editor.refreshLayers();
  };

  const onDuplicate = (obj) => {
    obj.clone((cloned) => {
      cloned.set({ left: (obj.left || 0) + 20, top: (obj.top || 0) + 20, evented: true });
      cloned.name = `${obj.name || typeLabel(obj.type)} copie`;
      canvas.add(cloned);
      canvas.discardActiveObject();
      canvas.setActiveObject(cloned);
      editor.requestRender();
      editor.pushHistory('Dublare strat');
      editor.refreshLayers();
    }, editor.getSerializationProps());
  };

  const onDelete = (obj) => {
    if (canvas.getActiveObject() === obj) canvas.discardActiveObject();
    canvas.remove(obj);
    editor.requestRender();
    editor.pushHistory('Stergere strat');
    editor.refreshLayers();
  };

  // Display index (0 = top/front) -> canvas index (0 = back). See mapping proof
  // in the orchestrator report.
  const onSortEnd = ({ oldIndex, newIndex }) => {
    if (oldIndex === newIndex) return;
    const list = editor.getObjects();
    const count = list.length;
    const obj = list[count - 1 - oldIndex];
    if (!obj) return;
    canvas.moveTo(obj, count - 1 - newIndex);
    editor.requestRender();
    editor.pushHistory('Reordonare straturi');
    editor.refreshLayers();
  };

  // Build rows in display order (front/top first = reverse of canvas z-order).
  const rows = [];
  for (let d = 0; d < n; d++) {
    const obj = objs[n - 1 - d];
    const editing = editingId === layerId(obj);
    rows.push({
      key: layerId(obj),
      displayIndex: d,
      editing,
      rowProps: {
        obj,
        name: obj.name || typeLabel(obj.type),
        type: obj.type,
        active: activeSet.has(obj),
        hidden: obj.visible === false,
        locked: isLocked(obj),
        editing,
        draftName,
        focusRename,
        onSelect,
        onStartRename: startRename,
        onDraftChange,
        onRenameKeyDown,
        onRenameBlur: commitRename,
        onToggleVis,
        onToggleLock,
        onDuplicate,
        onDelete,
      },
    });
  }

  return (
    <div className="de-lp" ref={rootRef}>
      <div className="de-lp-header">
        <span className="de-lp-header-title">Straturi</span>
        <div className="de-lp-header-actions">
          <button
            className="de-lp-icon-btn"
            disabled={!canGroup}
            title="Grupeaza (Ctrl+G)"
            onClick={() => groupSelection(editor)}
          >{groupIcon}</button>
          <button
            className="de-lp-icon-btn"
            disabled={!canUngroup}
            title="Anuleaza gruparea (Ctrl+Shift+G)"
            onClick={() => ungroupSelection(editor)}
          >{ungroupIcon}</button>
        </div>
      </div>
      {n === 0 ? (
        <div className="de-lp-empty">Niciun strat</div>
      ) : (
        <LayerList
          axis="y"
          lockAxis="y"
          useDragHandle
          distance={4}
          helperClass="de-lp-dragging"
          helperContainer={() => rootRef.current || document.body}
          onSortEnd={onSortEnd}
          rows={rows}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Registration seam — called once from features.js registerFeatures(editor).
// ---------------------------------------------------------------------------
export function registerLayersPanel(editor) {
  // Persist "handles hidden while locked" across undo + save/reload. (name /
  // visible / selectable / evented / lock* are already in the foundation's
  // BASE_SERIALIZATION_PROPS — no need to re-register those.)
  editor.registerSerializationProps(['hasControls']);

  // Group / ungroup shortcuts (foundation owns keyboard dispatch; we register).
  editor.registerCommand('mod+g', (ed) => groupSelection(ed));
  editor.registerCommand('mod+shift+g', (ed) => ungroupSelection(ed));

  // The panel renders in the LEFT properties column; order 900 keeps it below
  // the contextual property sections (a "layers zone" at the bottom).
  editor.addPanelSection({
    id: 'layers',
    order: 900,
    render: (ed) => <LayersPanel editor={ed} />,
  });
}

export default LayersPanel;
